<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/chemicals/custom">Custom Chemicals</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Chemical' : 'New Chemical' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Custom Chemical' : 'New Custom Chemical' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column -->
    <div class="col-lg-8">
      <!-- Basic Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Chemical Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name"
                     placeholder="e.g., Premium Foliar Blend"
                     [class.is-invalid]="isFieldInvalid('name')">
              <div class="invalid-feedback">Name is required (minimum 3 characters)</div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Category <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="category">
                <option *ngFor="let cat of chemicalCategories" [value]="cat.value">
                  {{ cat.label }}
                </option>
              </select>
              <div class="form-text small">{{ getCategoryDescription() }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Manufacturer</label>
              <input type="text" class="form-control" formControlName="manufacturer"
                     placeholder="e.g., ACME AgriChem">
            </div>
            <div class="col-md-6">
              <label class="form-label">EPA Number</label>
              <input type="text" class="form-control" formControlName="epaNumber"
                     placeholder="e.g., 12345-678">
            </div>
            <div class="col-md-6">
              <label class="form-label">Active Ingredient</label>
              <input type="text" class="form-control" formControlName="activeIngredient"
                     placeholder="e.g., Glyphosate (48.7%)">
            </div>
            <div class="col-md-6">
              <label class="form-label">Formulation</label>
              <input type="text" class="form-control" formControlName="formulation"
                     placeholder="e.g., Liquid Concentrate">
            </div>
          </div>
        </div>
      </div>

      <!-- Application Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Application</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-12">
              <label class="form-label">Default Application Rate <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="defaultRate"
                     placeholder="e.g., 2 gal/acre or 32 oz/acre"
                     [class.is-invalid]="isFieldInvalid('defaultRate')">
              <div class="invalid-feedback">Application rate is required</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Safety Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-shield-exclamation me-2"></i>Safety Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Signal Word</label>
              <select class="form-select" formControlName="signalWord">
                <option *ngFor="let sw of signalWords" [value]="sw.value">
                  {{ sw.label }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">REI (hours)</label>
              <input type="text" class="form-control" formControlName="rei"
                     placeholder="e.g., 12 hours">
              <div class="form-text small">Restricted Entry Interval</div>
            </div>
            <div class="col-md-4">
              <label class="form-label">PHI (days)</label>
              <input type="text" class="form-control" formControlName="phi"
                     placeholder="e.g., 7 days">
              <div class="form-text small">Pre-Harvest Interval</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Target Pests -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-bug me-2"></i>Target Pests</h5>
            <button
              *ngIf="targetPestsTags.length > 0"
              type="button"
              class="btn btn-link btn-sm text-muted p-0"
              (click)="clearAllPests()"
            >
              Clear all
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="tag-input-container">
            <div class="tags-display" *ngIf="targetPestsTags.length > 0">
              <span
                *ngFor="let pest of targetPestsTags; let i = index"
                class="tag-chip"
              >
                {{ pest }}
                <button type="button" class="tag-remove" (click)="removePestTag(i)" [attr.aria-label]="'Remove pest ' + pest">
                  <i class="bi bi-x"></i>
                </button>
              </span>
            </div>
            <div class="tag-input-row">
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newPestInput"
                [ngModelOptions]="{standalone: true}"
                placeholder="Type and press Enter to add..."
                (keydown)="onPestInputKeydown($event)"
              />
              <button
                type="button"
                class="btn btn-outline-primary"
                (click)="addPestTag()"
                [disabled]="!newPestInput.trim()"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            </div>
          </div>
          <small class="form-text text-muted">{{ targetPestsTags.length }} pest(s) added</small>
        </div>
      </div>

      <!-- Notes -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter class="form-control" formControlName="notes" rows="3"
                    placeholder="Any additional notes about this chemical..."></textarea>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Target Crops -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-tree me-2"></i>Target Crops <span class="text-danger">*</span></h5>
            <button
              *ngIf="cropsTags.length > 0"
              type="button"
              class="btn btn-link btn-sm text-muted p-0"
              (click)="clearAllCrops()"
            >
              Clear all
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="tag-input-container">
            <div class="tags-display" *ngIf="cropsTags.length > 0">
              <span
                *ngFor="let crop of cropsTags; let i = index"
                class="tag-chip"
              >
                {{ crop }}
                <button type="button" class="tag-remove" (click)="removeCropTag(i)" [attr.aria-label]="'Remove crop ' + crop">
                  <i class="bi bi-x"></i>
                </button>
              </span>
            </div>
            <div class="tag-input-row">
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newCropInput"
                [ngModelOptions]="{standalone: true}"
                placeholder="Type and press Enter to add..."
                (keydown)="onCropInputKeydown($event)"
                (focus)="onCropInputFocus()"
                (blur)="onCropInputBlur()"
              />
              <button
                type="button"
                class="btn btn-outline-primary"
                (click)="addCropTag()"
                [disabled]="!newCropInput.trim()"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            </div>
          </div>
          <!-- Crop suggestions dropdown -->
          <div class="crop-suggestions" *ngIf="showCropSuggestions && filteredCropSuggestions.length > 0"
               (mousedown)="$event.preventDefault()">
            <button
              type="button"
              class="suggestion-item"
              *ngFor="let crop of filteredCropSuggestions"
              (click)="addCropFromSuggestion(crop)"
            >
              {{ crop }}
            </button>
          </div>
          <small class="form-text text-muted">{{ cropsTags.length }} crop(s) added</small>

          <div *ngIf="cropsTags.length === 0" class="alert alert-warning small mt-2 mb-0">
            <i class="bi bi-exclamation-triangle me-1"></i>
            Please add at least one crop
          </div>
        </div>

        <!-- Form Actions -->
        <div class="card-footer p-3">
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving || form.invalid || cropsTags.length === 0">
              <span *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Chemical' : 'Create Chemical' }}
              </span>
              <span *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2"></span>Saving...
              </span>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()" [disabled]="isSaving">
              Cancel
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</form>
